import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  BriefcaseBusiness,
  Building2,
  FileText,
  History,
  Image,
  Link2,
  LucideIcon,
  Search,
  UserRound,
  X,
} from 'lucide-react';
import { searchService } from '../../services/searchService';
import { EntityIcon, entityKindOf } from '../ui';
import { SearchEntityType, SearchSuggestion } from '../../types/search';
import { useDebounce } from '../../hooks/useDebounce';

interface GroupedSuggestion {
  entityType: string;
  items: SearchSuggestion[];
}

const entityTypeIcons: Record<string, LucideIcon> = {
  stakeholder: UserRound,
  organization: Building2,
  assignment: BriefcaseBusiness,
  wiki_page: FileText,
  relationship: Link2,
  media_asset: Image,
};

const entityTypeLabels: Record<string, string> = {
  stakeholder: 'Stakeholders',
  organization: 'Organizations',
  assignment: 'Roles',
  wiki_page: 'Wiki',
  relationship: 'Relationships',
  media_asset: 'Media',
  name: 'People & Orgs',
  title: 'Pages',
  role: 'Roles',
};

interface GlobalSearchBarProps {
  autoFocus?: boolean;
  /** Show results in the flow (for a full-height sheet) rather than as a dropdown */
  inline?: boolean;
  /** Called after the bar navigates somewhere, e.g. to close a sheet */
  onNavigate?: () => void;
  /** Shown beside the field, above the results (e.g. a sheet's Cancel) */
  trailing?: React.ReactNode;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({ autoFocus = false, inline = false, onNavigate, trailing }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 200);

  // Recent searches live in localStorage and other screens (the search page) write them too,
  // so always read the stored list rather than trusting this component's copy
  const readRecentSearches = useCallback((): string[] => {
    try {
      const stored = localStorage.getItem('staketory_recent_searches');
      return stored ? JSON.parse(stored).slice(0, 5) : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    setRecentSearches(readRecentSearches());
  }, [readRecentSearches]);

  const saveRecentSearch = useCallback((term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    const updated = [trimmed, ...readRecentSearches().filter(s => s !== trimmed)].slice(0, 5);
    try {
      localStorage.setItem('staketory_recent_searches', JSON.stringify(updated));
    } catch {}
    setRecentSearches(updated);
  }, [readRecentSearches]);

  // Fetch suggestions
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    const fetchSuggestions = async () => {
      setIsLoading(true);
      try {
        const response = await searchService.getSuggestions(debouncedQuery.trim());
        if (!cancelled) {
          setSuggestions(response.suggestions);
          setSelectedIndex(-1);
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSuggestions();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  // Keyboard shortcut to focus search (the desktop bar only)
  useEffect(() => {
    if (inline) return;
    const handleGlobalKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const active = document.activeElement;
        const isInput = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement;
        if (!isInput) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKey);
    return () => document.removeEventListener('keydown', handleGlobalKey);
  }, [inline]);

  // Group suggestions by entityType
  const groupedSuggestions: GroupedSuggestion[] = useMemo(() => {
    const groups = new Map<string, SearchSuggestion[]>();
    for (const s of suggestions) {
      const key = s.entityType || s.type;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    }
    return Array.from(groups.entries()).map(([entityType, items]) => ({
      entityType,
      items: items.slice(0, 4),
    }));
  }, [suggestions]);

  const goToSearchPage = useCallback((searchTerm?: string) => {
    const q = (searchTerm || query).trim();
    if (!q) return;
    saveRecentSearch(q);
    setIsOpen(false);
    setQuery('');
    navigate(`/search?q=${encodeURIComponent(q)}`);
    onNavigate?.();
  }, [query, navigate, saveRecentSearch, onNavigate]);

  const handleSuggestionClick = useCallback((suggestion: SearchSuggestion) => {
    saveRecentSearch(suggestion.text);
    setIsOpen(false);
    setQuery('');
    onNavigate?.();

    // Navigate directly to entity if ID is provided
    if (suggestion.id) {
      const entityType = suggestion.entityType as SearchEntityType;
      switch (entityType) {
        case SearchEntityType.STAKEHOLDER:
          navigate(`/stakeholders/${suggestion.id}`);
          return;
        case SearchEntityType.ORGANIZATION:
          navigate(`/organizations/${suggestion.id}`);
          return;
        case SearchEntityType.WIKI_PAGE:
          navigate(`/wiki/${suggestion.id}`);
          return;
        default:
          break;
      }
    }

    // Fallback: search for the text
    navigate(`/search?q=${encodeURIComponent(suggestion.text)}`);
  }, [navigate, saveRecentSearch, onNavigate]);

  // Build flat navigation list for keyboard
  const flatCount = useMemo(() => {
    if (query.trim()) return suggestions.length;
    return recentSearches.length;
  }, [query, suggestions, recentSearches]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim() && selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else if (!query.trim() && selectedIndex >= 0 && selectedIndex < recentSearches.length) {
        goToSearchPage(recentSearches[selectedIndex]);
      } else {
        goToSearchPage();
      }
    } else if (e.key === 'Escape' && !inline) {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setRecentSearches(readRecentSearches());
    setIsOpen(true);
  };

  const clearQuery = () => {
    setQuery('');
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const showDropdown = (inline || isOpen) && (query.trim() ? true : recentSearches.length > 0);

  const getEntityIcon = (entityType: string): LucideIcon => entityTypeIcons[entityType] || Search;

  const rowClass = (selected: boolean) =>
    `flex w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] transition-colors ${
      inline ? 'h-11' : 'h-9'
    } ${selected ? 'bg-neutral-100 text-ink' : 'text-neutral-600 hover:bg-neutral-100 hover:text-ink'}`;

  return (
    <div ref={searchRef} className={`relative w-full font-ui ${inline ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
      {/* Search Input */}
      <div className="flex items-center gap-2">
        <div className="group relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 transition-colors group-focus-within:text-ink"
            strokeWidth={1.75}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder="Search"
            enterKeyHint="search"
            className={`block w-full rounded-full border-0 bg-neutral-100 pl-10 pr-14 text-ink placeholder:text-neutral-400 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25 ${
              inline ? 'h-11 text-[16px]' : 'h-10 text-[14px]'
            }`}
            data-testid="global-search-input"
            autoComplete="off"
            role="combobox"
            aria-label="Search"
            aria-expanded={showDropdown}
            aria-controls="global-search-results"
            aria-haspopup="listbox"
            aria-autocomplete="list"
          />
          <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
            {query && (
              <button
                type="button"
                onClick={clearQuery}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-ink"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            )}
            {!inline && !query && (
              <kbd className="hidden h-6 min-w-[24px] items-center justify-center rounded-md bg-white px-1.5 font-ui text-[11px] text-neutral-400 ring-1 ring-neutral-200 sm:inline-flex">
                /
              </kbd>
            )}
          </div>
        </div>
        {trailing}
      </div>

      {/* Results */}
      {showDropdown && (
        <div
          id="global-search-results"
          className={
            inline
              ? '-mx-1 mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain px-1'
              : 'absolute z-[60] mt-2 w-full min-w-[300px] animate-fade-up overflow-hidden rounded-2xl bg-white p-1.5 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]'
          }
          role="listbox"
        >
          {/* Loading indicator */}
          {isLoading && (
            <div className="mx-3 mb-1 h-0.5 overflow-hidden rounded-full bg-neutral-100">
              <div className="h-full w-3/5 animate-pulse rounded-full bg-primary-600" />
            </div>
          )}

          {/* Recent Searches */}
          {!query.trim() && recentSearches.length > 0 && (
            <div>
              <div className="px-3 pb-1 pt-2 text-[12px] text-neutral-400">Recent</div>
              {recentSearches.map((text, idx) => (
                <button
                  key={text}
                  onClick={() => goToSearchPage(text)}
                  className={rowClass(selectedIndex === idx)}
                  role="option"
                  aria-selected={selectedIndex === idx}
                >
                  <History className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                  <span className="truncate">{text}</span>
                </button>
              ))}
            </div>
          )}

          {/* Suggestions grouped by entity type */}
          {query.trim() && groupedSuggestions.length > 0 && (
            <div className={inline ? '' : 'max-h-80 overflow-y-auto'}>
              {groupedSuggestions.map((group) => {
                const Icon = getEntityIcon(group.entityType);
                const kind = entityKindOf(group.entityType);
                return (
                  <div key={group.entityType}>
                    <div className="px-3 pb-1 pt-2 text-[12px] text-neutral-400">
                      {entityTypeLabels[group.entityType] || group.entityType}
                    </div>
                    {group.items.map((suggestion) => {
                      const flatIdx = suggestions.indexOf(suggestion);
                      return (
                        <button
                          key={`${suggestion.text}-${suggestion.id || flatIdx}`}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={rowClass(selectedIndex === flatIdx)}
                          role="option"
                          aria-selected={selectedIndex === flatIdx}
                        >
                          {kind ? (
                            <EntityIcon kind={kind} size={inline ? 'sm' : 'xs'} icon={Icon} />
                          ) : (
                            <Icon className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                          )}
                          <span className="flex-1 truncate">{suggestion.text}</span>
                          {suggestion.id && (
                            <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* No results */}
          {query.trim() && !isLoading && suggestions.length === 0 && (
            <p className="px-3 py-6 text-center text-[13px] text-neutral-400">No suggestions</p>
          )}

          {/* Footer: search all */}
          {query.trim() && (
            <button
              onClick={() => goToSearchPage()}
              className={`mt-1 flex w-full items-center justify-between rounded-xl px-3 text-[14px] text-primary-600 transition-colors hover:bg-primary-50 ${
                inline ? 'h-11' : 'h-10'
              }`}
            >
              <span className="flex min-w-0 items-center gap-3">
                <Search className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                <span className="truncate">Search &ldquo;{query.trim()}&rdquo;</span>
              </span>
              {!inline && <span className="text-[12px] text-neutral-400">&#8629;</span>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
